<script setup lang="ts">
import {
  inject,
  Ref,
  ref,
  watchEffect,
} from 'vue';
import {
  CdrCard,
} from '@rei/cedar';
import { camelCase, snakeCase } from '../../common/utils';
import { sendClickAnalytics } from '../../utils';
import tags from '../../utils/globalTags';
import { useIcons } from '../../common/composables/useIcons';
import { apiCall } from '../../api/services/apiCalls';
import CenterCategoryContent from './CenterCategoryContent.vue';

type temp = {HelpCenterId: Ref<string>, HelpCenterName: Ref<string>, setHelpCenterValue: (name: string, id?:string) => void};

const { HelpCenterId, HelpCenterName } = inject<temp>('centerId');
const centerSections = ref([]);
const TopicArticles = ref<any[]>([]);
const activeCategory = ref<string>('');
const secondaryCategory = ref<string|null>(null);
const backButton = { helpCenterName: 'Go back', categoryIcon: 'IconArrowLeft', isBackButton: true };

const categoriesMetricHandler = () => {
  let linkName = `${tags.HELP_CENTER_UPPERCASE}_${snakeCase(HelpCenterName.value)}`;
  const isTertiary = secondaryCategory.value && secondaryCategory.value !== activeCategory.value;
  if (isTertiary) {
    linkName += `_${camelCase(secondaryCategory.value)}_${camelCase(activeCategory.value)}`;
  } else {
    linkName += `_${camelCase(activeCategory.value)}`;
  }
  const clickData = {
    pageName: '',
    linkName,
  };
  sendClickAnalytics(clickData);
};

const getSubsections = async () => {
  const helpCenters = await apiCall('default', 'help-topics', 'true', 'includeDetails');
  const nestedSection = helpCenters.filter((section: { children: any[]; }) => section.children.length);
  const { children } = nestedSection[0];
  centerSections.value = [...children];
  TopicArticles.value = [];
};

const setSecondaryTag = () => {
  if (!secondaryCategory.value && HelpCenterId.value === '') {
    secondaryCategory.value = activeCategory.value;
  }
};

const handleClick = ({
  answers, sections, header, helpCenterName, isBackButton,
}): void => {
  if (isBackButton) {
    secondaryCategory.value = null;
    getSubsections();
    return;
  }
  activeCategory.value = header ?? helpCenterName;
  setSecondaryTag();
  categoriesMetricHandler();
  if (!sections) {
    TopicArticles.value = [...answers];
    return;
  }
  centerSections.value = [backButton, ...sections];
  TopicArticles.value = [];
};

watchEffect(async () => {
  if (HelpCenterName.value && HelpCenterId.value === '') {
    getSubsections();
    return;
  }
  const response = HelpCenterId.value && await apiCall('default', `help-topics/helpCenterIds/${HelpCenterId.value}`, 'true', 'includeDetails');
  if (response.length) {
    const { sections } = response[0];
    centerSections.value = [...sections];
    TopicArticles.value = [];
  }
});
</script>

<template>
  <div
    v-if="centerSections.length"
    class="help-centers__column help-centers__column-secondary"
  >
    <div class="help-centers__categories">
      <div>
        <div
          v-for="(center, i) in centerSections"
          :key="i"
        >
          <CdrCard
            :class="`help-centers__card help-centers__categories-card
              ${center.header === activeCategory && 'help-centers__card--active'}
              ${center.isBackButton && 'help-centers__card--back'}
              `"
            @click="handleClick(center)"
          >
            <div
              v-if="center.categoryIcon"
              class="help-centers__icon-div help-centers__icon-div--sm"
            >
              <component
                :is="useIcons(center.categoryIcon || 'IconReturns')"
                class="help-centers__icon help-centers__icon--primary"
              />
            </div>
            <p>{{ center.header ?? center.helpCenterName }}</p>
          </CdrCard>
        </div>
      </div>
    </div>
    <CenterCategoryContent
      :data="TopicArticles"
    />
  </div>
  <div
    v-else
    class="help-centers__column help-centers__column--loading"
  >
    <p>Click on any topic to see the related articles. <br /></p>
  </div>
</template>
<style lang="scss">
@import 'src/main/style/helpCenter.scss';
</style>
